<template>
    <div class="content-block dx-card responsive-paddings" id="report">
        <DxForm
            id="form"
            :form-data="dataFilter"
            :read-only="load"
            @initialized=initializedForm
            :show-colon-after-label=true
        >
            <DxGroupItem>
                <DxSimpleItem data-field="title" :editor-options="{ readOnly: true }">
                    <DxLabel :text="$t('report.name')" />
                </DxSimpleItem>
            </DxGroupItem>

            <DxGroupItem :colCountByScreen="colCountByScreen2">
                <DxSimpleItem  data-field="periodType" editor-type="dxSelectBox"
                    :editor-options="{ dataSource: periodTypeDict, key:'code', displayExpr:'name', valueExpr:'code', disabled: (this.selectedReport && this.selectedReport.code === 'INSTALLED_CONSUMABLES') }"
                >
                    <DxLabel :text="$t('report.type')" />
                </DxSimpleItem>

                <DxGroupItem :alignItemLabels="alignLabels" :colCount="2">
                    <DxSimpleItem data-field="from" editor-type="dxDateBox" :editor-options=calendarOptionsFrom>
                        <DxLabel :text="$t('report.reportFilter.from')" />
                    </DxSimpleItem>

                    <DxSimpleItem data-field="to" editor-type="dxDateBox" :editor-options=calendarOptionsTo>
                        <DxLabel :text="$t('report.reportFilter.to')" />
                    </DxSimpleItem>
                </DxGroupItem>
            </DxGroupItem>

            <DxGroupItem :colCount="2">
                <DxSimpleItem data-field="location" editor-type="dxTagBox"
                    :editor-options="{ dataSource: locationsDict, displayExpr: 'name', displayValue: 'id', valueExpr: 'id' }"
                >
                    <DxLabel :text="$t('report.reportFilter.location')" />
                </DxSimpleItem>

                <DxSimpleItem data-field="printerType" editor-type="dxTagBox"
                    :editor-options="{ dataSource: printerTypesDict, displayExpr: 'name', displayValue: 'id', valueExpr: 'id' }"
                >
                    <DxLabel :text="$t('report.reportFilter.printerType')" />
                </DxSimpleItem>
            </DxGroupItem>

            <DxGroupItem :colCount="2">
                <DxSimpleItem data-field="colorType" editor-type="dxTagBox"
                    :editor-options="{ dataSource: colorTypesDict, displayExpr: 'name', displayValue: 'id', valueExpr: 'id' }"
                >
                    <DxLabel :text="$t('report.reportFilter.colorType')" />
                </DxSimpleItem>

                <DxSimpleItem data-field="paperFormat" editor-type="dxTagBox"
                    :editor-options="{ dataSource: paperFormatDict, displayExpr: 'name', displayValue: 'id', valueExpr: 'id' }"
                >
                    <DxLabel :text="$t('report.reportFilter.paperFormat')" />
                </DxSimpleItem>
            </DxGroupItem>
        </DxForm>

        <div class="form-btn">
            <aims2-button
                type="default"
                :text="$t('report.reportFilter.applyButton')"
                :disabled="load"
                :busy="load"
                @click="applyFilter()"
            />
        </div>

        <DxDataGrid
            class="data-grid dx-card"
            :data-source="dataSource"
            :height="gridHeight"
            @content-ready="onContentReady"
            @context-menu-preparing="addMenuItems"
            @initialized=initializedGrid
            @editor-preparing="onEditorPreparing"
            ref="dataGrid"
        >
            <DxHeaderFilter :visible="true" />
            <DxFilterRow :visible="true" />
            <DxFilterPanel :visible="showFilterPanel" />

            <DxPaging
                :enabled="true"
                :page-size="rowCountReport"
                :page-index="pageIndex"
            />
            <DxPager
                :visible="pagerEnabled"
                :allowed-page-sizes="pageSize"
                display-mode="full"
                :show-page-size-selector="true"
                :show-info="true"
                :show-navigation-buttons="false"
            />
            <DxSorting mode="multiple" />

            <DxColumn v-for="column in sortedColumns" :key="column.id" 
                :caption="column.caption"
                :data-field="column.dataField"
                :data-type="column.dataType"
                :format="column.format"
                :allow-filtering="column.allowFiltering"
                :allow-editing="column.allowEditing"
                :cell-template="column.cellTemplate"
                :width="column.width"
                :min-width="column.minWidth"
                true-text="Да" 
                false-text="Нет"
            />

            <DxToolbar>
                <DxItem
                    location="before"
                    widget="dxButton"
                    :options="editorsOptions.clearBtn"
                />
                <DxItem
                    v-if="canNotificationScheduleCreate"
                    location="before"
                    widget="dxButton"
                    :options="editorsOptions.notificationSchedule"
                />

                <DxItem
                    location="after"
                    widget="dxButtonGroup"
                    :options="editorsOptions.exportToFormat"
                />
            </DxToolbar>
        </DxDataGrid>

        <CustomColumnChooser
            :visible="visible"
            :columns="columns"
            :onApply="onApply"
            :onHiding="onHiding"
        />
    </div>
</template>

<script>
import baseView from "../base.vue";
import appInfo from "../../app-info";
import {
  PAGE_HEADER_HEIGHT,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_FILTER_ROW_HEIGHT,
  GRID_FILTER_PANEL_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';
import aims2Button from '@/components/active-button.vue';

import { mapCacheActions } from 'vuex-cache';

import {
    DxDataGrid, DxPager, DxPaging, DxFilterRow, DxHeaderFilter, DxFilterPanel, DxSorting, DxColumn, DxToolbar, DxItem
} from 'devextreme-vue/data-grid';
import {
    DxForm, DxSimpleItem, DxLabel, DxGroupItem
} from 'devextreme-vue/form';

import { notifier } from '@/shared/lib/notifier/index';

import { getDeviceAvailability, getDeviceCounters, getPartReplacements, getInstalledСonsumables, getUserLocations, download } from "@/services/api";
import { AclHandler } from "@/services/acl-handler";

import { DataBacked } from "@/mixins/dataBackedMixin";
import { DataDrid } from "@/mixins/dataDridMixin";
import { PeriodType } from "@/mixins/periodTypeMixin";

import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";

import { deviceAvailabilityReportConfig } from './config/report-device-availability.config';
import { deviceCountersReportConfig } from './config/report-device-counters.config';
import { partReplacementsReportConfig } from './config/report-part-replacements.config';
import { installedСonsumablesReportConfig } from './config/report-installed-consumables.config';

import CsvButton from "@/assets/csv.svg";
import { sizes } from "@/utils/media-query";

import qs from "qs";
import moment from "moment";
import { orderBy } from 'lodash';

// 
// TODO
// 
// возможно
// 1. оставить в этом компоненте только загрузку конфигов, а форму и таблицу вынести отдельно
// 2. переосмыслить switch или же от словарей функций в некий provider 

export default {
    name: 'Report',
    props: ['code'],
    mixins: [DataBacked, DataDrid, PeriodType],
    extends: baseView,
    components: {
        DxForm, DxSimpleItem, DxLabel, DxGroupItem, DxDataGrid, DxPager, DxPaging, DxFilterRow, DxHeaderFilter,
        CustomColumnChooser, DxFilterPanel, DxSorting, DxColumn, DxToolbar, DxItem, aims2Button
    },
    data() {
        return {
            isCloudMode: this.$store.getters.isCloudMode,
            selectedReport: null,
            gridLoad: false,
            load: false,
            alignLabels: false,
            dataSource: [],
            fromInstance: null,
            locationsDict: null,
            printerTypesDict: null,
            colorTypesDict: null,
            paperFormatDict: null,
            exportBtnGroup: null,
            pageIndex: 0,
            pagerEnabled: true,
        }
    },
    computed: {
        appUser() {
            return this.$store.getters.user;
        },
        aclUser() {
            return new AclHandler(this.appUser, this.isCloudMode);
        },
        canNotificationScheduleCreate() {
            return this.aclUser.hasGrantForOperation('BTN_EDIT_DEVICE', 'EXECUTE') || false;
        },
        showFilterPanel() {
            return this.aclUser.hasGrantForOperation('BTN_CLEAR_FILTER', 'EXECUTE') || false;
        },
        sumGap() {
            return PAGE_HEADER_HEIGHT + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
        },
        gridHeight() {
            const appHeightSize = this.$store.getters.appContainerSize.height;
            const padding = 40 * 2;

            return (appHeightSize || 800) - this.sumGap - padding;
        },
        gridBodyHeight() {
            const headerTolbarHeightReport = 47;

            return this.gridHeight - headerTolbarHeightReport - GRID_HEADER_HEIGHT - GRID_FILTER_ROW_HEIGHT - (this.showFilterPanel ? GRID_FILTER_PANEL_HEIGHT : 0) - GRID_PAGER_HEIGHT;
        },
        rowCountReport() {
            return this.countRows(this.gridBodyHeight);
        },
        reportConfig() {
            const selectedReport = this.selectedReport || {};

            switch (selectedReport.code) {
                case "DEVICE_AVAILABILITY":
                    return deviceAvailabilityReportConfig;
                case "DEVICE_COUNTERS":
                    return deviceCountersReportConfig;
                case "PART_REPLACEMENTS":
                    return partReplacementsReportConfig;
                case "INSTALLED_CONSUMABLES":
                    return installedСonsumablesReportConfig;

                default:
                    return { columns: [] };
            }
        },
        sortedColumns() {
            return orderBy(this.reportConfig.columns, ['sortOrder'], ['asc']);
        },
        editorsOptions() {
            return {
                clearBtn: {
                    text: this.$t('general.resetFilter'),
                    elementAttr: { id: 'resetFilter' },
                    onClick: () => {
                        this.dataGrid.clearFilter();
                    }
                },
                notificationSchedule : {
                    text: this.$t('report.btnNotificationSchedule'),
                    onClick: this.handleNotificationClick
                },
                exportToFormat : {
                    items: [{
                            icon: CsvButton,
                            hint: `${this.$t('report.export')} CSV`,
                            code: 'CSV'
                        }, {
                            icon: 'xlsxfile',
                            hint: `${this.$t('report.export')} XLSX`,
                            code: 'XLSX'
                        }
                    ],
                    selectionMode: 'none',
                    elementAttr: { id: 'export' },
                    disabled: true,
                    onInitialized: this.handleExportInitialized,
                    onItemClick: async (e) => {
                        await e.component.option('disabled', true);
                        notifier.showSuccess(this.$t('report.genFile'));

                        // eslint-disable-next-line no-unused-vars
                        let { title, periodType, ...filter } = this.dataFilter;

                        const columns = orderBy(this.dataGrid.state().columns, ['visibleIndex'], ['asc']).filter(el => el.visible).map(el => el.dataField);
                        filter = {...filter, ...{columns: columns}};
                        const param = { filter: JSON.stringify(filter || {}) };

                        try {
                            await download(`/reports/download/${this.selectedReport.id}/${e.itemData.code}?${qs.stringify(param, { arrayFormat: "repeat" })}`, `${title}.${e.itemData.code.toLowerCase()}`);

                            notifier.showSuccess(this.$t('general.uploadSuccess'));
                        } catch (err) {
                            this.onDataError(err);
                        }

                        await e.component.option('disabled', false);
                    },
                }
            };
        }
    },
    methods: {
        ...mapCacheActions(['getDictionaryItems', 'getReportsType']),
        onEditorPreparing(e) {
            this.applyFilterRowTimeout(e);
        },
        onContentReady(e) {
            this.addFitToPageHeightButton(e);
        },
        handleExportInitialized(e) {
            this.exportBtnGroup = e.component;
        },
        handleNotificationClick() {
            const state = this.dataGrid.state();
            this.stateSave(state);
            
            this.$router.push({
                name: "notification",
                params: { notification: { code: "REPORT", reportCode: this.selectedReport.code } }
            });
        },
        initializedForm(e) {
            this.fromInstance = e.component;
        },
        async loadReportFilter() {
            const reports = await this.getReportsType();
            this.selectedReport = reports.find(report => report.code === this.code);

            if (!this.selectedReport) {
                notifier.showError(this.$t('report.notFound'));
                this.$router.replace({ name: 'reports' });
                return false;
            }

            const reportCode = this.selectedReport.code;

            const defaultData = {
                title: '',
                periodType: 'LAST_MONTH',
                from: moment().startOf('month').format('YYYY-MM-DD'),
                to: moment().utc().format('YYYY-MM-DD'), // moment().format('YYYY-MM-DD')
                location: [],
                printerType: [],
                colorType: [],
                paperFormat: []
            };

            let data = { ...defaultData };
            this.gridStateKey = this.reportConfig.gridStateKey;

            if (reportCode === 'INSTALLED_CONSUMABLES') {
                const freeChoice = this.periodTypeDict.find(el => el.code === 'FREE_CHOICE');

                data.periodType = freeChoice.code;
                data.from = moment().utc().format('YYYY-MM-DD'); // moment().format('YYYY-MM-DD')
                data.to = moment().utc().format('YYYY-MM-DD'); // moment().format('YYYY-MM-DD')

                const commonOptions = {
                    disabled: true,
                    min: moment(new Date(2022, 0, 1))
                };

                this.calendarOptionsFrom = {
                    ...this.calendarOptionsFrom,
                    ...commonOptions
                };

                this.calendarOptionsTo = {
                    ...this.calendarOptionsTo,
                    ...commonOptions
                };
            }

            const savedFilter = this.$store.getters[this.reportConfig.filter.getter];
            if (savedFilter) {
                data = {
                    ...data,
                    ...savedFilter
                };
                this.dataFilter = {
                    ...data,
                    title: this.selectedReport.name
                };
                return true;
            }

            this.dataFilter = {
                ...data,
                title: this.selectedReport.name || ''
            };

            return true;
        },
        stateLoad() {
            let state = this.$store.getters.gridsStates;
            if (state && state[this.gridStateKey])
                state = this.$store.getters.gridsStates[this.gridStateKey];
            else
                state = {
                    pageIndex: 0,
                    pageSize: this.rowCountReport
                };

            if (!this.pageSize.includes(state.pageSize))
                state.pageSize = this.rowCountReport;

            this.dataGrid.state(state);
        },
        saveFilterReport() {
            if (!this.selectedReport) return;

            let filter = { ...this.dataFilter };

            const isFreeChoice = this.dataFilter.periodType === 'FREE_CHOICE';
            const isInstalledConsumables = this.selectedReport.code === 'INSTALLED_CONSUMABLES';

            if (!isFreeChoice || isInstalledConsumables) {
                delete filter.from;
                delete filter.to;
            }
            this.$store.commit(this.reportConfig.filter.mutation, filter);
        },
        async getReport() {
            if (this.exportBtnGroup)
                this.exportBtnGroup.option('disabled', true);

            this.load = true;

            try {
                // eslint-disable-next-line no-unused-vars
                const { title, periodType, ...filter } = this.dataFilter;
                const code = this.selectedReport.code;

                // TODO
                //    если планируется пагинация, то перейти на CustomStore
                //    добавить описание загрузки в config отчета, формата:
                    // dataSource: {
                    //     mode: 'array | customStore', // если есть переключение формата загрузки
                    //     load: getDeviceCounters,
                    //     prepareFilter: filter => filter
                    // }
                //    сделать фабричный метод createReportDataSource который принимает config и filter, смотрит на режим загрузки и возвращает то, что можно положить в DxDataGrid
                    // this.dataSource = await createReportDataSource({
                    //     reportConfig: this.reportConfig,
                    //     filter
                    // });
                // 
                const reportHandlers = {
                    DEVICE_AVAILABILITY: (filter) => getDeviceAvailability(filter),
                    DEVICE_COUNTERS: (filter) => getDeviceCounters(filter),
                    PART_REPLACEMENTS: (filter) => getPartReplacements(filter),
                    INSTALLED_CONSUMABLES: (filter) => {
                        // eslint-disable-next-line no-unused-vars
                        const { from, to, ...filterToday } = filter;
                        return getInstalledСonsumables(filterToday);
                    }
                };

                const fetchData = reportHandlers[code];

                this.dataSource = fetchData ? await fetchData(filter) : [];

                if (this.exportBtnGroup)
                    this.exportBtnGroup.option('disabled', this.dataSource.length === 0);
            } catch (error) {
                this.onDataError(error);
            } finally {
                this.load = false;
            }
        },
        handleResize() {
            const screen = sizes();
            if (screen['screen-large'] || screen['screen-medium'])
                this.alignLabels = false;
            else
                this.alignLabels = true;
        },
        handleReload() {
            const state = this.dataGrid.state();
            this.stateSave(state);
        },
        async applyFilter() {
            await this.getReport();
        }
    },
    async created() {
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('beforeunload', this.handleReload);
        this.handleResize();

        try {
            this.locationsDict = await getUserLocations();
            this.printerTypesDict = await this.getDictionaryItems('PRINTER_TYPE');
            this.colorTypesDict = await this.getDictionaryItems('COLOR_TYPE');
            this.paperFormatDict = await this.getDictionaryItems('PAPER_FORMAT');
            this.periodTypeDict = await this.getDictionaryItems('RPT_PERIOD_TYPE');

            const isReportLoaded = await this.loadReportFilter();
            if (!isReportLoaded) return;

            await this.stateLoad();

            document.title = `${appInfo.title} - ${this.$t('report.title')}`;

            this.$store.commit('setBreadcrumb', [{
                link: '/reports',
                title: this.$t('reports.title')
            }, {
                link: this.code ? `/report/${this.code}` : '/report',
                title: this.selectedReport.name
            }]);
        } catch (error) {
            this.onDataError(error);
        }
    },
    beforeDestroy() {
        this.saveFilterReport();
        if (!this.dataGrid) return;

        const state = this.dataGrid.state();
        this.stateSave(state);
    },
}
</script>

<style lang="scss">
#report {
    .form-btn {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
    }
}
.data-grid {
    margin-top: 24px;

    #export, #resetFilter {
        margin: 5px 5px 0;
    }
}
</style>